import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  LoaderFunction,
  useLoaderData,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useConfetti } from "@/hooks";
import { get, post } from "api";
import { Models, ShopDisplayType } from "api/types";
import { Button } from "atoms/button/Button";
import { Dropdown } from "atoms/dropdown/Dropdown";
import { Spinner } from "atoms/Spinner";
import { TextButton } from "atoms/text-button/TextButton";
import { AnimatedBackground } from "components/AnimatedBackground";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import {
  getProductTitle,
  getProductTitleQuantity,
  hasBeautyBankAvailable,
} from "services/HelperService";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";
import pluralize from "utils/pluralize";

type RewardSelectionItem = {
  platformUserMembershipId: string;
  membershipName: string;
  rewardGroup: Models<"MembershipRewardGroupModel">;
};

type LoaderData = Awaited<ReturnType<typeof membershipRewardsSelectionLoader>>;

const membershipRewardsSelectionLoader = async () => {
  const userMemberships = queryClient.fetchQuery(
    ["/platform-user-memberships"],
    () => get("/platform-user-memberships")
  );
  const platform = queryClient.fetchQuery(["/platform"], () =>
    get("/platform")
  );

  return waitObj({ userMemberships, platform });
};

export const MembershipRewardsSelectionPageLoader: LoaderFunction = () => {
  return membershipRewardsSelectionLoader();
};

const RewardGroupChooser = ({
  item,
  onNext,
  currentStep,
  totalSteps,
}: {
  item: RewardSelectionItem;
  onNext: (selectedIds: string[]) => void;
  currentStep: number;
  totalSteps: number;
}) => {
  const { triggerConfetti } = useConfetti();
  const isV3Enabled = true;
  const navigate = useNavigate();

  const availableSelections =
    item.rewardGroup.availableTreatmentsAmount ??
    item.rewardGroup.quantityPerPeriod;

  const [dropdownSelections, setDropdownSelections] = useState<string[]>(
    new Array(availableSelections).fill("")
  );
  const [dropdownTextValues, setDropdownTextValues] = useState<string[]>(
    new Array(availableSelections).fill("")
  );

  useEffect(() => {
    setTimeout(() => {
      triggerConfetti();
    }, 500);
  }, []);

  useEffect(() => {
    setDropdownSelections(new Array(availableSelections).fill(""));
    setDropdownTextValues(new Array(availableSelections).fill(""));
  }, [item, availableSelections]);

  const { mutate: submitTreatmentSelection, isLoading: isLoadingTreatments } =
    useMutation({
      mutationFn: async (rewardIds: string[]) => {
        return await post(
          "/platform-user-packages/{platformUserMembershipId}",
          {
            platformUserMembershipId: item.platformUserMembershipId,
            $data: rewardIds,
          }
        );
      },
      onSuccess: () => {
        // Invalidate queries to refresh the membership data
        queryClient.invalidateQueries(["/platform-user-memberships"]);
      },
      onError: (error) => {
        console.error(
          "Unable to save your treatment selection. Please try again or contact support if the problem continues.",
          error
        );
      },
    });

  const { mutate: submitBeautyBankSelection, isLoading: isLoadingBeautyBank } =
    useMutation({
      mutationFn: async (beautyBankId: string) => {
        return await post(
          "/platform-user-memberships/{platformUserMembershipId}/redeem/repeat-cash",
          {
            platformUserMembershipId: item.platformUserMembershipId,
            $data: beautyBankId,
          }
        );
      },
      onSuccess: () => {
        // Invalidate queries to refresh the beauty bank balance and membership data
        queryClient.invalidateQueries(["/user"]);
        queryClient.invalidateQueries(["/platform-user-memberships"]);
      },
      onError: (error) => {
        console.error(
          "Unable to save your beauty bank selection. Please try again or contact support if the problem continues.",
          error
        );
      },
    });

  const isLoading = isLoadingTreatments || isLoadingBeautyBank;

  const membershipRewards = item.rewardGroup.membershipRewards.map((mr) => ({
    id: mr.id,
    title:
      getProductTitle(mr.packageListingTitle, mr.packageName) +
      " " +
      getProductTitleQuantity(mr.quantity, mr.packageUnitType),
    description: mr.packageDescription,
  }));

  const hasBeautyBankCredit = hasBeautyBankAvailable(item.rewardGroup);
  const beautyBankCreditAmount = formatCurrency(
    item.rewardGroup.beautyBank?.creditAmount ?? 0,
    0
  );
  const beautyBankTerm = useFeatureFlag("beautyBankTerm");

  const availableOptions = hasBeautyBankCredit
    ? [
        ...membershipRewards,
        {
          id: "beautyBank",
          title: `${beautyBankTerm} RepeatCash - ${beautyBankCreditAmount}`,
          description: `Bank ${beautyBankCreditAmount} for use on future services - use anytime!`,
        },
      ]
    : membershipRewards;

  const dropdownOptions = [
    { id: "", name: "Select your treatment" },
    ...availableOptions.map((option) => ({
      id: option.id,
      name: option.title,
    })),
  ];

  const handleDropdownChange = (index: number, value: string) => {
    setDropdownSelections((prev) => {
      const newSelections = [...prev];
      newSelections[index] = value;
      return newSelections;
    });
  };

  const handleDropdownTextChange = (index: number, textValue: string) => {
    setDropdownTextValues((prev) => {
      const newTextValues = [...prev];
      newTextValues[index] = textValue;
      return newTextValues;
    });
  };

  const handleSubmit = async () => {
    const selectedIds = dropdownSelections.filter((id) => id !== "");

    if (selectedIds.length === 0) {
      onNext([]);
      return;
    }

    // Separate beauty bank selections from treatment selections
    const beautyBankSelections = selectedIds.filter(
      (id) => id === "beautyBank"
    );
    const treatmentSelections = selectedIds.filter((id) => id !== "beautyBank");

    try {
      // Handle beauty bank selections
      if (beautyBankSelections.length > 0 && item.rewardGroup.beautyBank?.id) {
        await new Promise<void>((resolve, reject) => {
          submitBeautyBankSelection(item.rewardGroup.beautyBank!.id, {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          });
        });
      }

      // Handle treatment selections
      if (treatmentSelections.length > 0) {
        await new Promise<void>((resolve, reject) => {
          submitTreatmentSelection(treatmentSelections, {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          });
        });
      }

      // If we get here, all submissions were successful
      onNext(selectedIds);
    } catch (error) {
      // Error handling is already done in the individual mutations
      console.error("Submission failed:", error);
    }
  };

  const handleSkip = () => {
    // Skip to next step
    onNext([]);
  };

  const content = (
    <div className="mt-[env(safe-area-inset-top)] h-screen flex-col">
      <div className="px-4 pt-12 text-center">
        <div className="pb-4 font-serif text-h2">
          You are now a {item.membershipName} member!
        </div>
        <div className="pb-8 text-body1 text-secondary">
          Choose your {pluralize(availableSelections, "pick", "picks")} for this
          month. Don&apos;t worry—you&apos;ll get to choose again next month
          when your membership renews!
        </div>

        {totalSteps > 1 && (
          <div className="pb-4 text-body2 text-secondary">
            Step {currentStep} of {totalSteps}
          </div>
        )}
      </div>

      <div className="flex-1 px-4">
        <div className="space-y-4">
          {Array.from({ length: availableSelections }, (_, index) => (
            <Dropdown
              key={index}
              label="Select your treatment"
              value={dropdownSelections[index]}
              textValue={dropdownTextValues[index]}
              options={dropdownOptions}
              setValue={(value) => handleDropdownChange(index, value)}
              setTextValue={(textValue) =>
                handleDropdownTextChange(index, textValue)
              }
            />
          ))}
        </div>
      </div>

      <div className="sticky bottom-[env(safe-area-inset-bottom)] w-full space-y-3 p-4">
        <Button fullWidth onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? "Saving..." : "Save picks"}
        </Button>
        <div className="flex justify-center">
          <TextButton onClick={handleSkip} disabled={isLoading}>
            Skip
          </TextButton>
        </div>
      </div>
    </div>
  );

  return isV3Enabled ? (
    <AnimatedBackground mode="light" className="min-h-screen">
      {content}
    </AnimatedBackground>
  ) : (
    <div className="bg-one">{content}</div>
  );
};

const MembershipRewardsSelectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { userMemberships, platform } = useLoaderData() as LoaderData;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectionQueue, setSelectionQueue] = useState<RewardSelectionItem[]>(
    []
  );

  const shopDisplayOrderId = searchParams.get("shopDisplayOrderId");
  const rewardProgress = searchParams.get("rewardProgress") || "0";
  const orderTransactionId = searchParams.get("orderTransactionId");

  const { data: shopDisplayOrder } = useQuery({
    queryKey: ["/orders/{shopDisplayOrderId}", { shopDisplayOrderId }],
    queryFn: () =>
      get("/orders/{shopDisplayOrderId}", {
        shopDisplayOrderId: shopDisplayOrderId || "",
      }),
    enabled: !!shopDisplayOrderId,
  });

  useEffect(() => {
    const buildSelectionQueue = () => {
      if (!shopDisplayOrder?.shopDisplayOrderLineItems || !userMemberships)
        return;

      const queue: RewardSelectionItem[] = [];

      const purchasedMembershipIds = shopDisplayOrder.shopDisplayOrderLineItems
        .filter((item) => item.shopDisplayMembershipId != null)
        .map((item) => item.shopDisplayMembershipId)
        .filter((id): id is string => id != null);

      for (const shopMembershipId of purchasedMembershipIds) {
        const platformUserMembership = userMemberships.find(
          (um) => um.membership.id === shopMembershipId
        );

        if (!platformUserMembership) continue;

        const selectableRewardGroups =
          platformUserMembership.membership.membershipRewardGroups?.filter(
            (rg: Models<"MembershipRewardGroupModel">) =>
              rg.hasSelectableRewards && (rg.availableTreatmentsAmount ?? 0) > 0
          ) || [];

        for (const rewardGroup of selectableRewardGroups) {
          queue.push({
            platformUserMembershipId:
              platformUserMembership.platformUserMembershipId,
            membershipName: platformUserMembership.membership.name,
            rewardGroup,
          });
        }
      }

      setSelectionQueue(queue);

      // Check if order contains treatments/packages for reward tracker
      const hasTreatmentsOrPackages =
        shopDisplayOrder.shopDisplayOrderLineItems.some(
          (item) =>
            item.displayType === ShopDisplayType.Package ||
            item.displayType === ShopDisplayType.Treatment
        );

      // If no selectable treatments available, automatically proceed forward
      if (queue.length === 0 && shopDisplayOrder && userMemberships) {
        // Use setTimeout to ensure navigation happens after component is fully mounted
        setTimeout(() => {
          // Check if spend-based rewards are enabled
          if (platform.platformSettings?.spendBasedRewardsEnabled) {
            const params = new URLSearchParams();
            params.set("rewardProgress", rewardProgress);
            if (shopDisplayOrderId) {
              params.set("shopDisplayOrderId", shopDisplayOrderId);
            }
            if (orderTransactionId) {
              params.set("orderTransactionId", orderTransactionId);
            }
            navigate(`/cart/checkout/reward?${params.toString()}`);
          } else {
            // Skip rewards tracker, go directly to home
            navigate("/home");
          }
        }, 0);
      }
    };

    buildSelectionQueue();
  }, [
    shopDisplayOrder,
    userMemberships,
    platform,
    navigate,
    rewardProgress,
    shopDisplayOrderId,
    orderTransactionId,
  ]);

  const handleNext = () => {
    if (currentIndex < selectionQueue.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      queryClient.invalidateQueries(["/platform-user-memberships"]);

      // Check if spend-based rewards are enabled
      if (platform.platformSettings?.spendBasedRewardsEnabled) {
        const params = new URLSearchParams();
        params.set("rewardProgress", rewardProgress);
        if (shopDisplayOrderId) {
          params.set("shopDisplayOrderId", shopDisplayOrderId);
        }
        if (orderTransactionId) {
          params.set("orderTransactionId", orderTransactionId);
        }
        navigate(`/cart/checkout/reward?${params.toString()}`);
      } else {
        // Skip rewards tracker, go directly to home
        navigate("/home");
      }
    }
  };

  if (selectionQueue.length === 0) {
    return <Spinner />;
  }

  const currentItem = selectionQueue[currentIndex];

  return (
    <RewardGroupChooser
      item={currentItem}
      onNext={handleNext}
      currentStep={currentIndex + 1}
      totalSteps={selectionQueue.length}
    />
  );
};

export default MembershipRewardsSelectionPage;
